import type { FC, ReactNode } from 'react'
import type { Principle } from '@/constants/about'

import { SplitText } from '@/components/motion/split-text'
import { Reveal } from '@/components/motion/reveal'
import { principles } from '@/constants/about'

interface ValueCardProps {
    value: Principle
    delay?: number
}

const ValueCard: FC<ValueCardProps> = ({ value, delay = 0 }): ReactNode => {
    return (
        <li className='w-full'>
            <Reveal y={20} delay={delay}>
                <article className='flex flex-col gap-4 border-t border-border pt-6 text-foreground'>
                    <span className='t-number'>
                        {value.number}
                    </span>

                    <h3 className='t-name text-left'>
                        {value.title}
                    </h3>

                    <p className='t-body max-w-[460px] text-left text-muted-foreground'>
                        {value.text}
                    </p>
                </article>
            </Reveal>
        </li>
    )
}

export const AboutValues: FC = (): ReactNode => {
    const leftColumn = principles.filter((_, index) => index % 2 === 0)
    const rightColumn = principles.filter((_, index) => index % 2 === 1)

    return (
        <section
            id='principles'
            className='relative z-[2] flex w-full flex-col gap-12 overflow-clip bg-background px-4 py-section-sm md:gap-16 lg:px-6'
        >
            <div className='flex w-full flex-col md:flex-row md:items-start md:justify-center md:gap-6'>
                <div className='flex w-full flex-col py-6 pr-6 md:max-w-[32%] md:flex-1 md:self-stretch'>
                    <h2 className='t-label max-w-[400px] text-left text-foreground'>
                        What I hold to
                    </h2>
                </div>

                <div className='flex w-full flex-col py-6 pr-6 md:flex-1'>
                    <SplitText
                        as='p'
                        text="Four simple rules that shape every project I take on, whether it is a company website or a system people rely on in an emergency."
                        effect='rise'
                        by='word'
                        stagger={0.01}
                        className='t-statement w-full max-w-full text-left text-foreground [text-wrap:balance] md:max-w-[90%]'
                    />
                </div>
            </div>

            <div className='flex w-full flex-col gap-12 md:flex-row md:gap-6 lg:gap-12'>
                <ul className='flex w-full flex-col gap-12 md:flex-1 lg:gap-16'>
                    {leftColumn.map((value) => (
                        <ValueCard key={value.title} value={value} />
                    ))}
                </ul>

                <ul className='flex w-full flex-col gap-12 md:flex-1 md:pt-section-sm lg:gap-16 lg:pt-section-md'>
                    {rightColumn.map((value) => (
                        <ValueCard key={value.title} value={value} delay={0.08} />
                    ))}
                </ul>
            </div>
        </section>
    )
}
